import { Flex, ToggleGroup } from '@utilitywarehouse/hearth-react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { FormikProps, useFormikContext } from 'formik';

import { EnergyTile } from '@components/ServiceTile';
import { BreakdownButtonConfig } from '@components/ServiceTile/types';
import { getEnergyTariffByType } from '@features/Energy/utils';
import { getTariffEndDate } from '@features/EnergyTariffForm/utils';
import {
  ChargeAmount,
  EnergyQuote,
  EnergyQuoteType,
} from '@generated/gql/graphql';
import { useFieldProps } from '@hooks/useFieldProps';
import { UpdatedTariffModalVariant } from '@src/types/bundle';
import { DateFormat } from '@src/types/index';
import { capitalizeFirstLetters } from '@utils/capitalizeFirstLetter';
import { formatCurrency } from '@utils/format';
import { trimPriceDecimals } from '@utils/price';

interface TariffFormValues {
  tariffType: EnergyQuoteType;
}

interface TariffRadioGroupProps {
  tariffs: EnergyQuote[];
  selectedPrice: Nullable<ChargeAmount>;
  variant: UpdatedTariffModalVariant;
  initialQuoteType?: EnergyQuoteType;
  onBreakdownClick: (variant: EnergyQuoteType) => void;
  isPassiveClient: boolean;
}

const getDiscountedPrice = (
  quoteType: EnergyQuoteType,
  selectedPrice: Nullable<ChargeAmount>,
  variant: UpdatedTariffModalVariant,
  initialQuoteType?: EnergyQuoteType
) => {
  if (
    variant !== UpdatedTariffModalVariant.productAdded ||
    !selectedPrice ||
    quoteType !== initialQuoteType
  ) {
    return undefined;
  }

  return trimPriceDecimals(formatCurrency(selectedPrice as ChargeAmount));
};

const getTariffBreakdown = (onClick: () => void): BreakdownButtonConfig => ({
  label: 'Tariff breakdown',
  variant: 'button' as const,
  onClick,
});

const TariffRadioGroup = ({
  tariffs,
  selectedPrice,
  initialQuoteType,
  variant,
  onBreakdownClick,
  isPassiveClient,
}: TariffRadioGroupProps) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitCount,
    setFieldValue,
  }: FormikProps<TariffFormValues> = useFormikContext();

  const getFieldProps = useFieldProps<TariffFormValues>({
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  });

  const fixed = getEnergyTariffByType(tariffs, EnergyQuoteType.Fixed);
  const tracker = getEnergyTariffByType(tariffs, EnergyQuoteType.Tracker);
  const variable = getEnergyTariffByType(tariffs, EnergyQuoteType.Variable);

  const { value, id } = getFieldProps('tariffType');

  return (
    <ToggleGroup
      asChild
      type="single"
      aria-label="Choose your energy tariff"
      value={value}
      id={id}
      onValueChange={(value: string) =>
        value && setFieldValue('tariffType', value)
      }
    >
      <Flex
        className={clsx('energy-tariff-modal__tariffSection', {
          'energy-tariff-modal__tariffSection--passive': isPassiveClient,
        })}
        spacing="xl"
      >
        {variable && (
          <EnergyTile
            discountedPrice={getDiscountedPrice(
              EnergyQuoteType.Variable,
              selectedPrice,
              variant,
              initialQuoteType
            )}
            price={formatCurrency(variable.monthlyCost as ChargeAmount)}
            description="Your energy rates will go up and down as the price cap changes"
            isSelected={values.tariffType === EnergyQuoteType.Variable}
            serviceName={capitalizeFirstLetters(variable.name)}
            breakdown={getTariffBreakdown(() =>
              onBreakdownClick(EnergyQuoteType.Variable)
            )}
            testId="Variable"
            value={EnergyQuoteType.Variable}
          />
        )}
        {tracker && (
          <EnergyTile
            description="Your rates move with the price cap, but with a guaranteed discount and fixed term."
            isSelected={values.tariffType === EnergyQuoteType.Tracker}
            discountedPrice={getDiscountedPrice(
              EnergyQuoteType.Tracker,
              selectedPrice,
              variant,
              initialQuoteType
            )}
            price={formatCurrency(tracker.monthlyCost as ChargeAmount)}
            serviceName={capitalizeFirstLetters(tracker.name)}
            breakdown={getTariffBreakdown(() =>
              onBreakdownClick(EnergyQuoteType.Tracker)
            )}
            testId="Tracker"
            value={EnergyQuoteType.Tracker}
          />
        )}
        {fixed && (
          <EnergyTile
            description={`Your energy rates will stay the same until ${dayjs(getTariffEndDate(fixed)).format(DateFormat.DisplayMonthName)}`}
            isSelected={values.tariffType === EnergyQuoteType.Fixed}
            discountedPrice={getDiscountedPrice(
              EnergyQuoteType.Fixed,
              selectedPrice,
              variant,
              initialQuoteType
            )}
            price={formatCurrency(fixed.monthlyCost as ChargeAmount)}
            serviceName={capitalizeFirstLetters(fixed.name)}
            breakdown={getTariffBreakdown(() =>
              onBreakdownClick(EnergyQuoteType.Fixed)
            )}
            testId="Fixed"
            value={EnergyQuoteType.Fixed}
          />
        )}
      </Flex>
    </ToggleGroup>
  );
};

export { TariffRadioGroup };
